import React, { useCallback, useContext, useEffect, useState } from 'react'
import { SimpleUserResponse } from '@/api/Identity/Types/UserResponse'
import { FormCheckboxField, FormSelectField } from '@/components/Form/FormField'
import { Spinner } from '@/components/Spinner'
import { successToast, ToastContext } from '@/components/Toast/Toast'
import { Button } from '@/components/ui/button'
import { getErrorResponse, useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { LeadsApiClient } from '@/utils/api/leadsApi'
import { useFetchOne } from '@/utils/api/useFetch'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'

interface AssignSGLFormValues {
  employee?: SimpleUserResponse
  ignoreChecks: boolean
}

interface OuterProps {
  isVisible: boolean
  leadId: string
  users: SimpleUserResponse[]
  isLoadingUsers: boolean
  exceptionMessage?: string

  onClose(): void
  onSubmit(values: AssignSGLFormValues): Promise<void>
  onEmployeeChange(): void
}
const AssignSGLModalUI: React.FC<FormikProps<AssignSGLFormValues> & OuterProps> = (props) => {
  const { isVisible, users, exceptionMessage, values, isSubmitting, handleSubmit, onClose } = props

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent size='lg'>
        <DialogHeader>
          <DialogTitle>Assign SGL</DialogTitle>
        </DialogHeader>

        <Form className='space-y-4'>
          <FormSelectField
            name='employee'
            label='Select employee'
            allowClear
            options={users}
            showSearch
          />

          <FormCheckboxField name='ignoreChecks' label='Ignore Checks' />

          {exceptionMessage && (
            <div className='rounded-md border border-destructive/30 bg-destructive/10 p-4'>
              <div className='text-sm font-medium text-destructive'>Exception</div>
              <div className='mt-1 text-sm text-destructive'>{exceptionMessage}</div>
            </div>
          )}

          <DialogFooter>
            <Button
              type='button'
              disabled={!values.employee?.id || isSubmitting}
              onClick={() => handleSubmit()}
            >
              Submit
              {isSubmitting && <Spinner data-icon='inline-start' />}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
const AssignSGLForm = withFormik<OuterProps, AssignSGLFormValues>({
  mapPropsToValues: () => ({
    employee: undefined,
    ignoreChecks: false,
  }),

  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },

  validate: (values) => {
    const errors: FormikErrors<AssignSGLFormValues> = {}

    if (!values.employee) {
      errors.employee = 'Required'
    }

    return errors
  },

  enableReinitialize: true,
})(AssignSGLModalUI)

interface AssignSGLModalProps {
  isVisible: boolean
  leadId: string
  onClose(): void
}

const AssignSGLModal = ({ isVisible, leadId, onClose }: AssignSGLModalProps) => {
  const setToast = useContext(ToastContext)
  const identityApiClient = useApiClient(IdentityApiClient)
  const leadsApiClient = useApiClient(LeadsApiClient)

  const [exceptionMessage, setExceptionMessage] = useState<string>()

  const usersData = useCallback(
    () =>
      identityApiClient.getUsers({
        kindId: 1,
        statusId: 1,
        roleName: 'SGL',
      }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const { data: users = [], isLoading: isLoadingUsers } = useFetchOne(usersData)

  useEffect(() => {
    if (isVisible) {
      setExceptionMessage(undefined)
    }
  }, [isVisible])

  const handleSubmit = async (values: AssignSGLFormValues) => {
    if (!values.employee) {
      return
    }

    setExceptionMessage(undefined)

    try {
      await leadsApiClient.assignSGL({
        leadId,
        userId: values.employee.id,
        ignoreChecks: values.ignoreChecks,
      })

      setToast(successToast('SGL assigned successfully.'))
      onClose()
    } catch (error) {
      const errorResponse = getErrorResponse(error)

      setExceptionMessage(errorResponse?.message ?? 'Something went wrong.')
    }
  }

  return (
    <AssignSGLForm
      isVisible={isVisible}
      leadId={leadId}
      users={users}
      isLoadingUsers={isLoadingUsers}
      exceptionMessage={exceptionMessage}
      onSubmit={handleSubmit}
      onClose={onClose}
      onEmployeeChange={() => setExceptionMessage(undefined)}
    />
  )
}

export default AssignSGLModal
